import amaLog from "../helpers/log";
import { getDevice } from "./device";
import { getSiteName, getHostName, resolveHost } from "./domain";
import { getModules } from '../modules/index';
import adConfig from "./adslots";
import { loadTopAdPlacement } from "../resource/resource";

type AdsData = {
  adsConfig?: IAdsConfig // response from https://se-ads-microservice.ads.allermedia.io/api/v1/config/get[site]
  adsUniqueId?: string;
};

export type AdsConfigJson = {
  siteName?: string;
  hostName?: string;
  pageMeta?: {
    isNativeAd?: boolean;
    hideAds?: '0' | '1';
    explicitContent?: boolean;
    device?: 'mobile' | 'desktop';
    firstVideoAutoStart?: string | boolean | null;

  };
  adsData?: AdsData;
  topAdTarget?: string;   // div id for top ad placement
  loadedByAdsLib?: LoadedByAdsLib;
  useAma?: boolean;
};
export const parseAdsConfig = (adsConfigJson: string | undefined): AdsConfigJson => adsConfigJson ? JSON.parse(adsConfigJson) : {};

export const useTopAds = () => window.ama?.useTopAds || window.useTopAds;

const resolveTrueFalse = (candidate: boolean | string | undefined, defaultVal: boolean = true): boolean => {
  if (typeof candidate === 'string') return candidate === 'true';
  if (typeof candidate === 'boolean') return candidate;
  return defaultVal;
};

export const loadConfig = async (adsConfigJson: AdsConfigJson) => {

  const {
    siteName,
    hostName,
    pageMeta = {},
    adsData = { adsConfig: window.ama?.accConfig },
  } = adsConfigJson;

  resolveHost(hostName);

  const host = `${getHostName()}`;
  const site = `${siteName || getSiteName()}`;

  amaLog('host site: ', host, site);

  const displayAds = !(pageMeta?.isNativeAd || pageMeta?.hideAds === '1') ? 'true' : 'false';
  const isNativeAd = pageMeta?.isNativeAd || pageMeta?.hideAds === '1';
  const explicitContent = !!pageMeta?.explicitContent;
  const firstVideoAutoStart = pageMeta?.firstVideoAutoStart === false ? false : true;
  const adsDevice = pageMeta?.device || getDevice();
  const adsUniqueId = typeof adsData.adsUniqueId !== 'undefined' ? `${adsData.adsUniqueId}` : undefined;
  let { adsConfig } = adsData;
  if (!adsConfig) {
    adsConfig = await adConfig.loadAccConfig(adsConfig);
  }

  if (adsConfig?.slimAds) {
    amaLog(`adsConfig.slimAds size: ${new Blob([JSON.stringify(adsConfig.slimAds)]).size} `);
    const expandedAds = expandAds(adsConfig.slimAds as any);
    adsConfig.ads = expandedAds;
  }

  const growthbooks = adsConfig?.growthbooks || [];
  const defaultEnableDemandManager = true;
  const defaultFilterAds = true;
  const adsUseDemandManager =
    typeof adsConfig?.enableDemandManager === 'undefined'
      ? defaultEnableDemandManager
      : adsConfig.enableDemandManager === 'true';

  const enableTopAd =
    typeof adsConfig?.enableWallpaperAds === 'undefined'
      ? true
      : adsConfig.enableWallpaperAds === 'true';
  window.ama = {
    siteName: site,
    hostName: host,
    adsUniqueId: window.ama?.adsUniqueId || window.adsUniqueId || adsUniqueId,
    displayAds: `${displayAds}`,
    adsUseDemandManager: `${adsUseDemandManager}`,
    device: `${adsDevice}`,
    filterAds: `${window.ama?.filterAds ?? defaultFilterAds}`,
    isNativeAd: `${isNativeAd}`,
    explicitContent: explicitContent,
    firstVideoAutoStart: firstVideoAutoStart,
    topAdFrequency: adsConfig?.topAdFrequency,
    ...window.ama,
    useTopAds: enableTopAd,
  }
  const { useGrowthbook } = getModules();

  if (useGrowthbook) {
    window.ama.growthbooks = growthbooks;
  }
  window.device = `${adsDevice}`; // Needed for demandManager
  if (useTopAds()) {
    const { topAdTarget = `top_ad-${window.ama?.adsUniqueId || window.adsUniqueId}` } = adsConfigJson;

    await loadTopAdPlacement(topAdTarget);
  }

  return adsConfig;
}

type Size = [number, number];
type Sizes = Size[];
type Placements = string[];

type SlimSizes = {
  d?: number[]; // indices to SizeList
  m?: number[];
};

type SlimEntry = {
  p: number; // index in Placements
  s: SlimSizes;
  t: number; // index in Placements (same as placement)
};

type SlimmedAd = {
  sizes: Sizes;
  placements: Placements;
  data: SlimEntry[];
};

interface ExpandedAd {
  placement: string;
  sizes: {
    desktop: Size[];
    mobile: Size[];
  };
  target: string;
};

export const expandAds = (slimmed: SlimmedAd): any[] => {
  const { sizes, placements, data } = slimmed;

  return data.map((entry) => {
    const desktop = entry.s.d?.map((i) => sizes[i]) ?? [];
    const mobile = entry.s.m?.map((i) => sizes[i]) ?? [];

    return {
      placement: placements[entry.p],
      sizes: {
        desktop,
        mobile,
      },
      target: placements[entry.t],
    };
  });
};

